"use client"

import { useState, useMemo } from "react"
import { RotateCcw, AlertTriangle, CheckCircle2 } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn } from "./formato"

const T = {
  es: {
    datosServicio: "Datos del servicio",
    limpiar: "Limpiar",
    empleados: "Nº de empleados por turno",
    horasTurno: "Horas por turno",
    costeHora: "Coste/hora medio (€)",
    serviciosMes: "Nº de servicios al mes",
    ingresosServicio: "Ingresos medios por servicio (€)",
    resultados: "Resultados",
    costeServicio: "Coste de personal por servicio",
    formula: (empleados: number, horas: number, coste: number) =>
      `${empleados} empleados × ${horas}h × ${coste}€/h`,
    costeMensual: "Coste mensual",
    costeEmpleadoServicio: "Coste/empleado/servicio",
    ratioPersonal: "Ratio de personal",
    ideal: "Ideal: 25-35%",
    analisis: "Análisis",
    eficiente:
      "Tu ratio de personal es muy eficiente. Asegúrate de que la calidad del servicio no se resiente por falta de equipo.",
    saludable: "Tu ratio de personal está dentro del rango saludable para hostelería (25-35%).",
    alto: "Tu ratio de personal es alto. Valora optimizar turnos, aumentar ingresos por servicio o revisar la plantilla.",
  },
  en: {
    datosServicio: "Service details",
    limpiar: "Reset",
    empleados: "Staff per shift",
    horasTurno: "Hours per shift",
    costeHora: "Average cost per hour (€)",
    serviciosMes: "Services per month",
    ingresosServicio: "Average revenue per service (€)",
    resultados: "Results",
    costeServicio: "Staff cost per service",
    formula: (empleados: number, horas: number, coste: number) =>
      `${empleados} staff × ${horas}h × €${coste}/h`,
    costeMensual: "Monthly cost",
    costeEmpleadoServicio: "Cost per employee per service",
    ratioPersonal: "Labour cost ratio",
    ideal: "Target: 25-35%",
    analisis: "Analysis",
    eficiente:
      "Your labour cost ratio is very lean. Make sure service quality isn’t suffering from being short-staffed.",
    saludable: "Your labour cost ratio is within the healthy range for hospitality (25-35%).",
    alto: "Your labour cost ratio is high. Consider tightening your rotas, increasing revenue per service or reviewing your headcount.",
  },
}

const defaultValues = {
  empleados: 4,
  horasTurno: 6,
  costeHora: 12,
  serviciosMes: 50,
  ingresosPorServicio: 1200,
}

export default function CostePersonalCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const [empleados, setEmpleados] = useState(defaultValues.empleados)
  const [horasTurno, setHorasTurno] = useState(defaultValues.horasTurno)
  const [costeHora, setCosteHora] = useState(defaultValues.costeHora)
  const [serviciosMes, setServiciosMes] = useState(defaultValues.serviciosMes)
  const [ingresosPorServicio, setIngresosPorServicio] = useState(
    defaultValues.ingresosPorServicio
  )

  const results = useMemo(() => {
    const costePorServicio = empleados * horasTurno * costeHora
    const costeMensual = costePorServicio * serviciosMes
    const ingresosMensuales = ingresosPorServicio * serviciosMes
    const ratioPersonal =
      ingresosMensuales > 0 ? (costeMensual / ingresosMensuales) * 100 : 0
    const costeEmpleadoServicio = empleados > 0 ? costePorServicio / empleados : 0

    return {
      costePorServicio,
      costeMensual,
      ingresosMensuales,
      ratioPersonal,
      costeEmpleadoServicio,
    }
  }, [empleados, horasTurno, costeHora, serviciosMes, ingresosPorServicio])

  const reset = () => {
    setEmpleados(defaultValues.empleados)
    setHorasTurno(defaultValues.horasTurno)
    setCosteHora(defaultValues.costeHora)
    setServiciosMes(defaultValues.serviciosMes)
    setIngresosPorServicio(defaultValues.ingresosPorServicio)
  }

  const getRatioColor = (pct: number) => {
    if (pct <= 0) return { text: "text-zinc-400", bg: "bg-zinc-100" }
    if (pct < 25) return { text: "text-emerald-600", bg: "bg-emerald-50" }
    if (pct <= 35) return { text: "text-lime-600", bg: "bg-lime-50" }
    return { text: "text-red-600", bg: "bg-red-50" }
  }

  const ratioStyle = getRatioColor(results.ratioPersonal)

  const inputClass =
    "w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent transition-shadow"

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.datosServicio}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.empleados}
            </label>
            <input
              type="number"
              value={empleados || ""}
              onChange={(e) => setEmpleados(parseInt(e.target.value) || 0)}
              placeholder="4"
              min="0"
              step="1"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.horasTurno}
            </label>
            <input
              type="number"
              value={horasTurno || ""}
              onChange={(e) => setHorasTurno(parseFloat(e.target.value) || 0)}
              placeholder="6"
              min="0"
              step="0.5"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.costeHora}
            </label>
            <input
              type="number"
              value={costeHora || ""}
              onChange={(e) => setCosteHora(parseFloat(e.target.value) || 0)}
              placeholder="12"
              min="0"
              step="any"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.serviciosMes}
            </label>
            <input
              type="number"
              value={serviciosMes || ""}
              onChange={(e) => setServiciosMes(parseInt(e.target.value) || 0)}
              placeholder="50"
              min="0"
              step="1"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.ingresosServicio}
            </label>
            <input
              type="number"
              value={ingresosPorServicio || ""}
              onChange={(e) =>
                setIngresosPorServicio(parseFloat(e.target.value) || 0)
              }
              placeholder="1200"
              min="0"
              step="any"
              className={inputClass}
            />
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* Main KPI */}
          <div className="rounded-2xl p-6 text-center bg-violet-50">
            <p className="text-sm font-medium text-zinc-600 mb-1">
              {t.costeServicio}
            </p>
            <p className="text-5xl font-bold text-violet-600">
              {idioma === "en" ? eurosEn(results.costePorServicio, 0) : `${results.costePorServicio.toFixed(0)}€`}
            </p>
            <p className="text-sm text-zinc-500 mt-2">
              {t.formula(empleados, horasTurno, costeHora)}
            </p>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.costeMensual}</p>
              <p className="text-xl font-bold text-zinc-900">
                {idioma === "en" ? `€${results.costeMensual.toLocaleString("en-GB", { maximumFractionDigits: 2 })}` : `${results.costeMensual.toLocaleString("es-ES")}€`}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <p className="text-xs text-zinc-500 mb-1">{t.costeEmpleadoServicio}</p>
              <p className="text-xl font-bold text-zinc-900">
                {idioma === "en" ? eurosEn(results.costeEmpleadoServicio, 0) : `${results.costeEmpleadoServicio.toFixed(0)}€`}
              </p>
            </div>
          </div>

          {/* Ratio */}
          <div className={`rounded-2xl p-4 ${ratioStyle.bg}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-900">
                  {t.ratioPersonal}
                </p>
                <p className="text-xs text-zinc-500">{t.ideal}</p>
              </div>
              <p className={`text-3xl font-bold ${ratioStyle.text}`}>
                {results.ratioPersonal.toFixed(1)}%
              </p>
            </div>
            {/* Visual bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-zinc-400 mb-1">
                <span>0%</span>
                <span>25%</span>
                <span>35%</span>
                <span>50%</span>
              </div>
              <div className="relative h-3 rounded-full overflow-hidden">
                <div className="absolute inset-0 flex">
                  <div className="w-[50%] bg-emerald-200" />
                  <div className="w-[20%] bg-lime-200" />
                  <div className="w-[30%] bg-red-200" />
                </div>
                {results.ratioPersonal > 0 && (
                  <div
                    className="absolute top-0 bottom-0 w-1 rounded-full bg-zinc-800 shadow-sm transition-all duration-300"
                    style={{
                      left: `${Math.min(results.ratioPersonal, 50) * 2}%`,
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Insights */}
          <div className="border-t border-zinc-200 pt-4 space-y-2">
            <p className="text-sm font-medium text-zinc-700">{t.analisis}</p>
            {results.ratioPersonal <= 25 && results.ratioPersonal > 0 && (
              <div className="flex items-start gap-2 text-sm text-zinc-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <p>
                  {t.eficiente}
                </p>
              </div>
            )}
            {results.ratioPersonal > 25 && results.ratioPersonal <= 35 && (
              <div className="flex items-start gap-2 text-sm text-zinc-600">
                <CheckCircle2 className="w-4 h-4 text-lime-500 shrink-0 mt-0.5" />
                <p>
                  {t.saludable}
                </p>
              </div>
            )}
            {results.ratioPersonal > 35 && (
              <div className="flex items-start gap-2 text-sm text-zinc-600">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p>
                  {t.alto}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
